import type { PropsWithChildren } from 'react';
import { useMemo } from 'react';

import type { Config } from '~app/config';

import type { ApplicationsApi } from '~entities/application';
import {
  APPLICATIONS_API_KEY,
  GatewayApplicationsApi,
} from '~entities/application';
import { DOCUMENT_API_KEY, GatewayDocumentApi } from '~entities/document';
import type { MerchantApi } from '~entities/merchant';
import { GatewayMerchantApi, MERCHANT_API_KEY } from '~entities/merchant';
import { GatewayOrdersApi, ORDERS_API_KEY } from '~entities/order';
import type { PaymentsApi } from '~entities/payment/api';
import { PAYMENTS_API_KEY, GatewayPaymentsApi } from '~entities/payment/api';
import type { PosApi } from '~entities/pos/api';
import { POS_API_KEY, GatewayPosApi } from '~entities/pos/api';
import { GatewayProductApi } from '~entities/product';
import { PRODUCT_API_KEY } from '~entities/product/api/product-api';
import type { ReportApi } from '~entities/report';
import { GatewayReportApi, REPORT_API_KEY } from '~entities/report';
import { GatewaySummaryApi, SUMMARY_API_KEY } from '~entities/summary';

import { httpClient } from '~shared/api/http-client';
import type { ScpClient } from '~shared/api/scp/client';
import { ApiContext, ApiMap } from '~shared/providers/use-api';

type Props = {
  scpClient: ScpClient;
  config: Config;
};

export const ApiProvider = (props: PropsWithChildren<Props>) => {
  const { scpClient, config, children } = props;
  const paymentsApi: PaymentsApi = useMemo(
    () =>
      new GatewayPaymentsApi({
        scpClient,
        host: config.apiHost,
      }),
    [config, scpClient]
  );
  const applicationsApi: ApplicationsApi = useMemo(
    () =>
      new GatewayApplicationsApi({
        scpClient,
        host: config.apiHost,
      }),
    [config, scpClient]
  );
  const summaryApi = useMemo(
    () =>
      new GatewaySummaryApi({
        scpClient,
        host: config.apiHost,
      }),
    [config, scpClient]
  );
  const posApi: PosApi = useMemo(
    () => new GatewayPosApi({ scpClient, host: config.apiHost }),
    [config, scpClient]
  );
  const merchantApi: MerchantApi = useMemo(
    () => new GatewayMerchantApi({ scpClient, host: config.apiHost }),
    [config, scpClient]
  );
  const reportApi: ReportApi = useMemo(
    () => new GatewayReportApi({ scpClient, host: config.apiHost }),
    [config, scpClient]
  );
  const documentApi = useMemo(
    () =>
      new GatewayDocumentApi({
        host: config.apiHost,
        client: httpClient,
      }),
    [config]
  );
  const productApi = useMemo(
    () => new GatewayProductApi({ scpClient, host: config.apiHost }),
    [scpClient, config]
  );
  const ordersApi = useMemo(
    () => new GatewayOrdersApi({ scpClient, host: config.apiHost }),
    [scpClient, config]
  );

  const apis = useMemo(() => {
    const apisMap = new ApiMap({
      [PAYMENTS_API_KEY]: paymentsApi,
      [APPLICATIONS_API_KEY]: applicationsApi,
      [SUMMARY_API_KEY]: summaryApi,
      [POS_API_KEY]: posApi,
      [MERCHANT_API_KEY]: merchantApi,
      [REPORT_API_KEY]: reportApi,
      [DOCUMENT_API_KEY]: documentApi,
      [PRODUCT_API_KEY]: productApi,
      [ORDERS_API_KEY]: ordersApi,
    });

    return apisMap;
  }, [
    applicationsApi,
    paymentsApi,
    summaryApi,
    posApi,
    merchantApi,
    reportApi,
    documentApi,
    productApi,
    ordersApi,
  ]);

  return <ApiContext.Provider value={apis}>{children}</ApiContext.Provider>;
};
